/**
 *
 * ClassesDetail
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useTranslation } from "react-i18next";

import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { flushState, loadClass } from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemData,
} from "./selectors";

// antd component
import { Col, Row, Tabs, message } from "antd";
const { TabPane } = Tabs;

// assets
//import LogoClient from 'images/logo.svg';
//import CustomIcons from 'components/CustomIcons';

// components
import Preloader from "components/Preloader";
import CustomPageHeader from "components/CustomPageHeader";
import CustomForm from "components/Forms/CustomForm";
import InfoCard from "components/InfoCard";

// Custom Tabs
import TabStudents from "components/Tabs/TabStudents";
import TabCourses from "components/Tabs/TabCourses";
import RenderPopconfirm from "components/RenderPopconfirm";

// helper for forms
import { isEqualFormValues } from "utils/forms/forms.utils";

export function ClassesDetail({
  flushState,
  // reducer main props
  loading,
  itemData,
  loadClass,
  // custom props
  currentId,
  update,
  updatedItem,
  // form actions and data
  form,
  generalForm,
  // parent states
  formHaveChanges,
  setFormHaveChanges,
  currentElement,
  setCurrentElement,
}) {
  useInjectReducer({ key: "classesDetail", reducer });
  useInjectSaga({ key: "classesDetail", saga });

  const { t, i18n } = useTranslation();

  const newKey = "new";

  const [initID, setInitID] = useState(currentId);

  // states for tabs toggle
  const [activeTab, setActiveTab] = useState("general");
  const [stayActiveTab, setStayActiveTab] = useState(false);

  // states for main data
  const [currentStudents, setCurrentStudents] = useState([]);
  const [currentCourses, setCurrentCourses] = useState([]);

  // Form initialization for each tab
  const { customForm, resetForm, submitForm, setFormData } = form;

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // reset data in the general form
    resetForm();
    if (!currentId || currentId == newKey) {
      // Set default data for NEW Item
      setCurrentElement(false);
      setCurrentStudents([]);
      setActiveTab("general");
    } else if (currentId && currentId !== newKey) {
      setCurrentElement(false);
      setCurrentStudents([]);
      loadClass(currentId);
    }

    setInitID(currentId);
  }, [currentId]);

  // Set data for opened item
  useEffect(() => {
    if (itemData || updatedItem) {
      // check if updated data the same as current data
      let initData;
      if (currentId == updatedItem?._id || currentId == newKey) {
        initData = updatedItem;
      } else {
        initData =
          itemData?._id == updatedItem?._id
            ? updatedItem || itemData
            : itemData;
      }

      setInitID(initData?._id);
      setCurrentElement(initData);

      // set students list
      if (
        JSON.stringify(currentStudents) !== JSON.stringify(initData?.students)
      ) {
        setCurrentStudents(initData?.students);
      }

      // set courses list
      if (
        JSON.stringify(currentCourses) !== JSON.stringify(initData?.courses)
      ) {
        setCurrentCourses(initData?.courses);
      }

      // reset data in the general form
      setFormData(initData);
    }
  }, [itemData, updatedItem]);

  // Clearing the state after unmounting a component
  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
      setCurrentElement(false);
      setCurrentStudents([]);
    };
  }, []);

  // General Form action
  const onSubmitForm = data => {
    const updatedData = {
      ...currentElement,
      ...data,
    };
    update(updatedData);
    setStayActiveTab(false);
    setFormHaveChanges(false);
    message.success(t("notifications.dataSaved"));
  };

  const onFormChange = useCallback((changedFields, allFields) => {
    checkFromChanges(true);
  }, []);

  const onFormValuesChange = useCallback(
    (changedValues, allValues) => {
      const formVals = Object.values(allValues);
      const formHasAnyVals = formVals.some(
        val => val != "" && val != undefined && val != null,
      );
      const isEqualValuesWithElement = isEqualFormValues(
        generalForm,
        currentElement,
        allValues,
      );

      if (
        (!isEqualValuesWithElement && currentElement) ||
        (!isEqualValuesWithElement && formHasAnyVals && !currentElement)
      ) {
        checkFromChanges(true);
      } else {
        checkFromChanges(false);
      }
    },
    [currentElement, generalForm, i18n?.language],
  );

  // Set if form have any changes
  const checkFromChanges = haveChange => {
    setFormHaveChanges(haveChange);
    setStayActiveTab(haveChange);
  };

  /*
   * Сhange the way to switch tabs,
   * if there are changes in the form in the active tab
   */
  const onChangeTab = (key, event) => {
    if (formHaveChanges) {
      event.stopPropagation();
      !stayActiveTab && setActiveTab(key);
    } else {
      setActiveTab(key);
    }
  };

  /*
   * Custom tab title. Added Popconfirm window,
   * if there are changes in the form in the active tab
   */
  const renderTabTitle = useCallback(
    (tabTitle, key) => (
      <RenderPopconfirm
        showPopup={formHaveChanges}
        title={tabTitle}
        key={key}
        onCancel={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          // update data in the general form
          setFormData(currentElement);
        }}
        onConfirm={() => {
          setActiveTab(key);
          setStayActiveTab(false);
          setFormHaveChanges(false);
          submitForm();
        }}
      />
    ),
    [formHaveChanges, customForm, currentElement],
  );

  // Remove/Add (students, teachers, parents)
  const updateTableList = (array, type) => {
    if (array) {
      const updatedElem = { ...currentElement };
      if (type === "students") {
        updatedElem.students = array;
      } else if (type === "parents") {
        updatedElem.parents = array;
      } else if (type === "courses") {
        updatedElem.courses = array;
      }
      update(updatedElem);
    }
  };

  /*
   * TABS Content
   */
  const tabAdvanced = () => {
    return (
      <TabPane
        disabled
        tab={renderTabTitle(t("pages.advancedSettings"), "gestion_avancee")}
        key="gestion_avancee"
      >
        {t("pages.advancedSettings")}
      </TabPane>
    );
  };

  // Rendering another tabs
  const otherTabs = () => {
    return (
      <>
        {
          <TabPane
            tab={renderTabTitle(t("labels.courses"), "cours")}
            key="cours"
          >
            <TabCourses
              courses={currentCourses}
              classId={currentElement?._id}
              classLevel={currentElement?.level}
              classYear={currentElement?.year}
              classTitle={currentElement?.title}
              updateList={array => updateTableList(array, "courses")}
              canAddNew
            />
          </TabPane>
        }
        {
          <TabPane
            tab={renderTabTitle(t("labels.students"), "etudiants")}
            key="etudiants"
          >
            <TabStudents
              students={currentStudents}
              updateList={array => updateTableList(array, "students")}
            />
          </TabPane>
        }
        {tabAdvanced()}
      </>
    );
  };

  // render function
  return (
    <div className="details-container">
      <Helmet>
        <title>{t("labels.class")}</title>
        <meta name="description" content="" />
      </Helmet>
      {!initID && loading && <Preloader />}
      {!loading && initID && (
        <>
          <CustomPageHeader
            title={
              currentElement?.title && initID !== newKey
                ? `${currentElement?.title}`
                : t("classes.newClass")
            }
            subTitle={t("labels.class")}
            subTitlePosition="top"
            //extraActions={[]}
          />
          <Row gutter={[0, 0]}>
            <Col xs={24}>
              <Tabs
                defaultActiveKey={activeTab}
                activeKey={activeTab}
                className="details-tabs"
                onTabClick={onChangeTab}
              >
                <TabPane
                  tab={renderTabTitle(t("pages.generalInfo"), "general")}
                  key="general"
                  forceRender
                >
                  <Row>
                    <Col span={24}>
                      <CustomForm
                        name={`class-general-form-${initID}`}
                        form={customForm}
                        dataSource={generalForm}
                        handleSubmit={(data) => onSubmitForm(data)}
                        // handleChange={onFormChange}
                        handleValuesChange={onFormValuesChange}
                        labelCol={24}
                        wrapperCol={24}
                        disabeled={!formHaveChanges}
                      />
                    </Col>
                  </Row>
                  <Row gutter={(15, 15)} style={{ marginTop: "15px" }}>
                    <Col sm={24} md={24} lg={12} xl={12}>
                      <InfoCard
                        title={t("courses.labelNameOf")}
                        editACtion={
                          !formHaveChanges &&
                          initID !== newKey &&
                          (() => setActiveTab("cours"))
                        }
                      >
                        {`${
                          (currentElement?.courses &&
                            currentElement?.courses.length) ||
                          0
                        } ${t("classes.infoCardLabelCourses")}`}
                      </InfoCard>
                    </Col>
                    <Col sm={24} md={24} lg={12} xl={12}>
                      <InfoCard
                        title={t("students.labelNameOf")}
                        editACtion={
                          !formHaveChanges &&
                          initID !== newKey &&
                          (() => setActiveTab("etudiants"))
                        }
                      >
                        {`${
                          (currentStudents && currentStudents.length) || 0
                        } ${t("classes.infoCardLabelStudents")}`}
                      </InfoCard>
                    </Col>
                  </Row>
                </TabPane>
                {initID && initID !== newKey && otherTabs()}
              </Tabs>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

ClassesDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  update: PropTypes.func,
  loading: PropTypes.bool,
  currentId: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemData: makeSelectItemData(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadClass: currnetId => dispatch(loadClass(currnetId)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(ClassesDetail);
